import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { installGlobalApiInterceptors } from './utils/api';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { ToastProvider } from './contexts/ToastContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { FocusModeProvider } from './contexts/FocusModeContext';
import { ErrorHandlerProvider } from './contexts/ErrorHandlerContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { ForceLightThemeOnLanding } from './components/ForceLightThemeOnLanding';
import { InstallBanner } from './components/InstallBanner';
import { ServiceWorkerUpdateBanner } from './components/ServiceWorkerUpdateBanner';
import { SentryInit } from './components/SentryInit';
import { AmplitudeInit } from './components/AmplitudeInit';
import { GoogleAdsInit } from './components/GoogleAdsInit';
import './styles.css';
import App from './App';
import Editor from './Editor';
import { Settings } from './components/Settings';
import { Admin } from './components/admin/Admin';
import { AdminLayout } from './components/admin/AdminLayout';
import { Users } from './components/admin/Users';
import { Suggestions } from './components/admin/Suggestions';
import { Exports } from './components/admin/Exports';
import { WordImports } from './components/admin/WordImports';
import { CharactersAnalyses } from './components/admin/CharactersAnalyses';
import { ChapterAnalyses } from './components/admin/ChapterAnalyses';
import { FeatureTracking } from './components/admin/FeatureTracking';
import { LLMCalls } from './components/admin/LLMCalls';
import { CreditsActivity } from './components/admin/CreditsActivity';
import { UserDetail } from './components/admin/UserDetail';
import { UserFeedback } from './components/admin/UserFeedback';
import { InvitationCodes } from './components/admin/InvitationCodes';
import { FeatureFlagsAdmin } from './components/admin/FeatureFlagsAdmin';
import { WaitingListAdmin } from './components/admin/WaitingListAdmin';
import { ObservabilityEventsAdmin } from './components/admin/ObservabilityEventsAdmin';
import { StyleNotesAdmin } from './components/admin/StyleNotesAdmin';
import { OnboardingAdmin } from './components/admin/OnboardingAdmin';
import { ImpersonationActionsAdmin } from './components/admin/ImpersonationActionsAdmin';
import { SiteSettings } from './components/admin/SiteSettings';
import { AllUsersAdmin } from './components/admin/AllUsersAdmin';
import { ResourceDetail } from './components/ResourceDetail';
import { ResourcesTable } from './components/ResourcesTable';
import { DocumentsTable } from './components/DocumentsTable';
import { ProjectNotes } from './components/ProjectNotes';
import { ProjectSettings } from './components/ProjectSettings';
import { ProjectSharing } from './components/ProjectSharing';
import { ProjectTrash } from './components/ProjectTrash';
import { ForgotPassword } from './components/ForgotPassword';
import { ResetPassword } from './components/ResetPassword';
import { Signup } from './components/Signup';
import { Login } from './components/Login';
import { WaitingList } from './components/WaitingList';
import { DocumentViewer } from './components/DocumentViewer';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { Project } from './Project';
import { AppWrapper } from './components/AppWrapper';
import { RegisterPage } from './components/RegisterPage';
import { LoginPage } from './components/LoginPage';
import { ProjectRedirect } from './components/ProjectRedirect';
import { ProjectTimeline } from './components/ProjectTimeline';
import { TagsManager } from './components/TagsManager';
import { DocumentationLanding } from './components/DocumentationLanding';
import { WorldbuildingLanding } from './components/WorldbuildingLanding';
import { StructureLanding } from './components/StructureLanding';
import { ExternalRedirect } from './components/ExternalRedirect';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { DocumentMeta } from './components/DocumentMeta';
import { UseCasePage } from './components/Landing/UseCasePage';
import { captureUtmsOnLoad } from './utils/analytics';
import { installPwaLaunchConsumer } from './utils/pwa-launch';
import { PwaLaunchNavigation } from './components/PwaLaunchNavigation';

const Landing = React.lazy(() => import('./components/Landing/Landing').then(module => ({ default: module.Landing })));

installGlobalApiInterceptors();
installPwaLaunchConsumer();
// Persist UTM/click params before the router or any redirect can drop the
// query string, and regardless of cookie consent (storing ≠ sending).
captureUtmsOnLoad();

const root = ReactDOM.createRoot(document.getElementById('root')!);

const hideInitialSpinner = (): void => {
  const spinner = document.getElementById('initial-loading-spinner');
  if (spinner) {
    spinner.style.opacity = '0';
    spinner.style.transition = 'opacity 0.2s ease-out';
    setTimeout(() => {
      spinner.classList.add('hidden');
    }, 200);
  }
};

const hideInitialSpinnerWhenAppReady = (): void => {
  const root = document.getElementById('root');
  if (!root) {
    hideInitialSpinner();
    return;
  }

  if (root.childElementCount > 0) {
    hideInitialSpinner();
    return;
  }

  const observer = new MutationObserver(() => {
    if (root.childElementCount > 0) {
      observer.disconnect();
      hideInitialSpinner();
    }
  });

  observer.observe(root, { childList: true });
  setTimeout(() => {
    observer.disconnect();
    hideInitialSpinner();
  }, 5000);
};

root.render(
  <React.StrictMode>
    <AppErrorBoundary>
    <BrowserRouter>
      <ThemeProvider>
      <LocaleProvider>
        <CookieConsentProvider>
          <SentryInit />
          <AmplitudeInit />
          <GoogleAdsInit />
          <AuthProvider>
            <ErrorHandlerProvider>
              <AppWrapper>
                <NotificationsProvider>
                  <SettingsProvider>
                    <FocusModeProvider>
                      <ToastProvider>
                      <OnboardingProvider>
                      <ForceLightThemeOnLanding />
                      <CookieConsentBanner />
                      <InstallBanner />
                      <ServiceWorkerUpdateBanner />
                      <DocumentMeta />
              <PwaLaunchNavigation />
              <Routes>
                <Route path="/" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div>Loading...</div></div>}><Landing /></React.Suspense>} />
                <Route path="/for-novelists" element={<UseCasePage slug="for-novelists" />} />
                <Route path="/for-journalists" element={<UseCasePage slug="for-journalists" />} />
                <Route path="/for-screenwriters" element={<UseCasePage slug="for-screenwriters" />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/waitinglist" element={<WaitingList />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/desk" element={<App />}>
                <Route index element={null} />
                <Route path="import" element={null} />
                <Route path="trash" element={null} />
                <Route path="notes" element={null} />
                <Route path="notes/new" element={null} />
                <Route path="settings" element={<Settings />} />
                  <Route path="notifications" element={null} />
                  <Route path="shared-with-me" element={null} />
                  <Route path="usage-and-billing" element={null} />
                </Route>
                <Route path="/projects" element={<App />} />
                <Route path="/documents" element={<App />} />
                <Route path="/projects/:projectId" element={<Project />}>
                  <Route index element={<ProjectRedirect />} />
                  <Route path="documents" element={<DocumentsTable />} />
                  <Route path="documents/:documentId" element={<DocumentViewer />} />
                  <Route path="documents/:documentId/:panel" element={<DocumentViewer />} />
                  <Route path="notes" element={<ProjectNotes />} />
                  <Route path="resources" element={<ResourcesTable />} />
                  <Route path="resources/:resourceId" element={<ResourceDetail />} />
                  <Route path="documentation" element={<DocumentationLanding />} />
                  <Route path="worldbuilding" element={<WorldbuildingLanding />} />
                  <Route path="structure" element={<StructureLanding />} />
                  <Route path="timeline" element={<ProjectTimeline />} />
                  <Route path="tags" element={<TagsManager />} />
                  <Route path="settings" element={<ProjectSettings />} />
                  <Route path="sharing" element={<ProjectSharing />} />
                  <Route path="trash" element={<ProjectTrash />} />
                </Route>
                <Route path="/documents/:documentId" element={<DocumentViewer />} />
                <Route path="/documents/:documentId/:panel" element={<DocumentViewer />} />
                <Route path="/admin">
                  <Route path="documents/:documentId" element={<DocumentViewer />} />
                  <Route path="documents/:documentId/:panel" element={<DocumentViewer />} />
                  <Route element={<AdminLayout />}>
                    <Route index element={<Admin />} />
                    <Route path="users" element={<Users />} />
                    <Route path="users/:id" element={<UserDetail />} />
                    <Route path="suggestions" element={<Suggestions />} />
                    <Route path="style-notes" element={<StyleNotesAdmin />} />
                    <Route path="exports" element={<Exports />} />
                    <Route path="word-imports" element={<WordImports />} />
                    <Route path="characters-analyses" element={<CharactersAnalyses />} />
                    <Route path="chapter-analyses" element={<ChapterAnalyses />} />
                    <Route path="feature-tracking" element={<FeatureTracking />} />
                    <Route path="llm-calls" element={<LLMCalls />} />
                    <Route path="credits-activity" element={<CreditsActivity />} />
                    <Route path="user-feedback" element={<UserFeedback />} />
                    <Route path="invitation-codes" element={<InvitationCodes />} />
                    <Route path="feature-flags" element={<FeatureFlagsAdmin />} />
                    <Route path="waiting-list" element={<WaitingListAdmin />} />
                    <Route path="impersonation-actions" element={<ImpersonationActionsAdmin />} />
                    <Route path="observability-events" element={<ObservabilityEventsAdmin />} />
                    <Route path="onboarding" element={<OnboardingAdmin />} />
                    <Route path="site-settings" element={<SiteSettings />} />
                    <Route path="all-users" element={<AllUsersAdmin />} />
                  </Route>
                </Route>
                <Route path="/promo/2026" element={<ExternalRedirect url="https://www.youtube.com/watch?v=dQw4w9WgXcQ" />} />
              </Routes>
                      </OnboardingProvider>
                      </ToastProvider>
                    </FocusModeProvider>
                  </SettingsProvider>
                </NotificationsProvider>
            </AppWrapper>
            </ErrorHandlerProvider>
          </AuthProvider>
        </CookieConsentProvider>
      </LocaleProvider>
      </ThemeProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>
);

hideInitialSpinnerWhenAppReady();
